import type { CreateReviewData } from "@/hooks/data/reviews/useCreateReview";

const OFFLINE_REVIEWS_QUEUE_KEY = "sewar_offline_reviews_queue";

export interface QueuedReviewItem extends CreateReviewData {
  id: string;
  queuedAt: number;
}

let isSyncing = false;

/**
 * Retrieves the list of reviews stored offline in localStorage.
 */
export const getOfflineReviewsQueue = (): QueuedReviewItem[] => {
  try {
    const raw = localStorage.getItem(OFFLINE_REVIEWS_QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error("Failed to read offline reviews queue:", error);
    return [];
  }
};

/**
 * Saves a review to the offline localStorage queue.
 */
export const saveReviewToOfflineQueue = (data: CreateReviewData): QueuedReviewItem => {
  const queue = getOfflineReviewsQueue();
  const newItem: QueuedReviewItem = {
    ...data,
    id: `offline_review_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    queuedAt: Date.now(),
  };

  queue.push(newItem);

  try {
    localStorage.setItem(OFFLINE_REVIEWS_QUEUE_KEY, JSON.stringify(queue));
    console.log(`[OfflineReviews] Saved review to offline queue. Total queued: ${queue.length}`);
  } catch (error) {
    console.error("Failed to save review to offline queue in localStorage:", error);
  }

  return newItem;
};

/**
 * Removes a specific review from the offline queue.
 */
export const removeReviewFromOfflineQueue = (id: string): void => {
  try {
    const queue = getOfflineReviewsQueue();
    const updated = queue.filter((item) => item.id !== id);
    localStorage.setItem(OFFLINE_REVIEWS_QUEUE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error("Failed to remove review from offline queue:", error);
  }
};

/**
 * Sends a single review to the backend.
 */
const sendSingleReview = async (review: CreateReviewData): Promise<boolean> => {
  const { customer_name, rating, review_text, type, product_id, lat, lan } = review;
  const payload: Record<string, any> = {
    customer_name,
    rating,
    review_text,
    type: type || "branch",
  };

  if (product_id) payload.product_id = product_id;
  if (lat !== undefined && lat !== null) payload.lat = lat;
  if (lan !== undefined && lan !== null) payload.lan = lan;

  const response = await fetch("https://back.sewargourmet.com/api/v1/reviews", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  });

  return response.ok;
};

/**
 * Synchronizes all queued offline reviews one by one in the background.
 */
export const syncOfflineReviews = async (): Promise<void> => {
  if (isSyncing) return;
  if (typeof navigator !== "undefined" && !navigator.onLine) return;

  const queue = getOfflineReviewsQueue();
  if (queue.length === 0) return;

  isSyncing = true;
  console.log(`[OfflineReviews] Starting sync of ${queue.length} offline review(s) in background...`);

  try {
    // Process reviews one by one sequentially
    for (const item of queue) {
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        console.warn("[OfflineReviews] Connection lost during review sync, pausing.");
        break;
      }

      try {
        const success = await sendSingleReview(item);
        if (success) {
          removeReviewFromOfflineQueue(item.id);
          console.log(`[OfflineReviews] Successfully synced review for ${item.customer_name}`);
        } else {
          console.warn(`[OfflineReviews] Server returned error for review ${item.id}, will retry later.`);
          // If server error, stop and retry on next sync cycle
          break;
        }
      } catch (err) {
        console.warn(`[OfflineReviews] Network error while syncing review ${item.id}:`, err);
        break;
      }

      // Small delay between requests to avoid burst rate limiting
      await new Promise((resolve) => setTimeout(resolve, 400));
    }
  } finally {
    isSyncing = false;
  }
};

/**
 * Initializes the background listener for offline review syncing.
 */
export const initOfflineReviewsSync = (): (() => void) => {
  if (typeof window === "undefined") return () => {};

  const handleOnline = () => {
    console.log("[OfflineReviews] Internet connection restored. Triggering offline review sync...");
    syncOfflineReviews();
  };

  window.addEventListener("online", handleOnline);

  // Run initial sync check
  if (navigator.onLine) {
    syncOfflineReviews();
  }

  // Periodic fallback check every 60 seconds
  const intervalId = window.setInterval(() => {
    if (navigator.onLine && getOfflineReviewsQueue().length > 0) {
      syncOfflineReviews();
    }
  }, 60000);

  return () => {
    window.removeEventListener("online", handleOnline);
    window.clearInterval(intervalId);
  };
};
